import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Form, InputGroup, Pagination, FormGroup, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { CartGateway } from "../../gateways/CartGateway";
import { SetCookie } from "../../common/CookieHelper";
import { useGlobal } from "../../contexts/GlobalContext";
import { Utils } from "../../common/Utils";
import { FireStoreProduct } from "../../model/ProducModel";
import { FireStoreCategory } from "../../model/CategoryModel";
import { ProductGateway } from "../../gateways/ProductGateway";
import { CategoryGateway } from "../../gateways/CategoryGateway";
import { CartItem } from "../../model/CartModel";
import { GetCartCookieKey } from "../../gateways/SiteGateway";

const ProductListing: React.FC = () => {
  const [products, setProducts] = useState<FireStoreProduct[]>([]);
  const [categories, setCategories] = useState<FireStoreCategory[]>([]);
  //const [cart, setCart] = useState<CartItem[]>([]);
  const { categoryId, deliveryLocation } = useParams(); // Get categoryId from URL
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12; // Number of products per page
  const [searchTerm, setSearchTerm] = useState(""); // Search box state
  const { cart, updateCart, settings, user, setScannedDeliveryLocation } = useGlobal();
  const [loadingState, setLoadingState] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState<boolean>(true);

  const navigate = useNavigate();

  useEffect(() => {

    const fetchProducts = async () => {

      const productList = await ProductGateway.GetProducts("Active", categoryId as string);

      // Sort products by category name, then by title
      productList.sort((a, b) => a.product.category.localeCompare(b.product.category) || a.product.name.localeCompare(b.product.name));

      setProducts(productList);
      setLoading(false);
    };

    const fetchCategories = async () => {
      const categoryList = await CategoryGateway.GetCategories();

      // Sort categories alphabetically
      categoryList.sort((a, b) => a.category.name.localeCompare(b.category.name));

      setCategories(categoryList);
    };

    if (deliveryLocation) {
      setScannedDeliveryLocation(deliveryLocation);
    }
    fetchProducts();
    fetchCategories();
  }, [categoryId, setScannedDeliveryLocation, deliveryLocation]);

  if (loading || !settings) {
    return (
      <Container className="main-container mt-4 text-center">
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Fetching products...</p>
      </Container>
    );
  }

  // Pagination logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;

  // Filter products by search term
  const filteredProducts = products.filter(firestoreProduct =>
    firestoreProduct.product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(products.length / productsPerPage);
  const maxAttributes = Math.max(...currentProducts.map(p => p.product.attributes?.length || 0));
  // const cardMinHeight = 420 + maxAttributes * 25 + (settings.hasPrice ? 45 : 0); //

  const addToCart = async (firestoreProduct: FireStoreProduct, quantity: number, selectedAttributes: { [key: string]: string }) => {
    setLoadingState(prev => ({ ...prev, [firestoreProduct.id]: true })); // Start loading

    setTimeout(async () => {
      const existingItem = cart.cartItems.find(item => ProductGateway.GetKey(item.id, item.attributes) === ProductGateway.GetKey(firestoreProduct.id, selectedAttributes));
      const updatedCart = existingItem
        ? cart.cartItems.map(item => (ProductGateway.GetKey(item.id, item.attributes) === ProductGateway.GetKey(firestoreProduct.id, selectedAttributes) ? { ...item, quantity: item.quantity + quantity, attributes: { ...selectedAttributes } } : item))
        : [...cart.cartItems, { id: firestoreProduct.id, name: firestoreProduct.product.name, attributes: { ...selectedAttributes }, quantity }];

      if (user)
        await CartGateway.SaveCart({...cart, cartItems: updatedCart as CartItem[]}, user);
      else
        SetCookie(GetCartCookieKey, JSON.stringify({...cart, cartItems: updatedCart}), 7);

      updateCart();
      setLoadingState(prev => ({ ...prev, [firestoreProduct.id]: false })); // Stop loading
    }, 1000);
  };

  const handleAddToCart = async (firestoreProduct: FireStoreProduct) => {
    const quantityInput = document.getElementById(`quantity-${firestoreProduct.id}`) as HTMLInputElement;
    const quantity = parseInt(quantityInput.value, 10) || 1;

    const selectedAttributes: { [key: string]: string } = {};

    if (firestoreProduct.product.attributes) {
      let allAttributesSelected = true;

      firestoreProduct.product.attributes.forEach(attr => {
        const selectElement = document.getElementById(`attr-${firestoreProduct.id}-${attr.name}`) as HTMLSelectElement;
        if (selectElement && selectElement.value) {
          selectedAttributes[attr.name] = selectElement.value;
          selectElement.classList.remove("is-invalid");
        } else {
          allAttributesSelected = false; // Flag missing selection
          selectElement.classList.add("is-invalid");
        }
      });

      // Validate attributes before proceeding
      if (!allAttributesSelected) {
        return;
      }

    }

    addToCart(firestoreProduct, quantity, selectedAttributes);
  };

  return (
    <Container className="main-container mt-4">
      <Row>
        {categories && categories.length > 1 ? (
          <>
            <Col md={3}>
              <h4>Filters</h4>
              <FormGroup className="mb-3">
                <h5>Product Search</h5>
                <Form.Control
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </FormGroup>
              <FormGroup className="mb-3">
                <h5>Category Filter</h5>
                <ul className="list-group">
                  {categories.map((firestoreCategory, index) => (
                    <li
                      key={index}
                      className={`list-group-item ${categoryId === firestoreCategory.id ? "active" : ""}`}
                      onClick={() => navigate(Utils.GetUrl(`/products/${firestoreCategory.id}`))}
                      style={{ cursor: "pointer" }}
                    >
                      {firestoreCategory.category.name}
                    </li>
                  ))}
                </ul>
              </FormGroup>
            </Col>
          </>) :
          (
            <Row>
              <Col md={5}>
                <FormGroup className="mb-3 d-flex align-items-center">
                  <h5 className="mb-0 me-3 text-nowrap">Product Search</h5>
                  <Form.Control
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>
          )}
        <Col>
          {categories.map((firestoreCategory) => {
            const categoryProducts = currentProducts.filter(
              (firestoreProduct) => firestoreProduct.product.category === firestoreCategory.id
            );

            return categoryProducts.length > 0 ? (
              <div key={firestoreCategory.id}>
                <h5 className="mt-3">{firestoreCategory.category.name}</h5>
                <Row>
                  {categoryProducts.map((firestoreProduct) => (
                    <Col xs={6} sm={6} md={4} lg={3} key={firestoreProduct.id}>
                      <Card
                        className="mb-2 mb-sm-3 p-2 p-sm-3 d-flex flex-column"
                        style={{ borderRadius: "15px", backgroundColor: "#f8f9fa" }}
                      >
                        <Card.Img
                          variant="top"
                          src={firestoreProduct.product.imageUrl !== "" ? firestoreProduct.product.imageUrl : "/images/icons/gallery.png"}
                          style={{ cursor: "pointer" }}
                          onClick={() => navigate(Utils.GetUrl(`/product/${firestoreProduct.id}`))}
                        />
                        <Card.Body className="d-flex flex-column">
                          <Card.Title className="responsive-title" style={{ cursor: "pointer" }} onClick={() => navigate(Utils.GetUrl(`/product/${firestoreProduct.id}`))}>
                            {firestoreProduct.product.name}
                          </Card.Title>
                          {settings.hasPrice && (
                            <Card.Text className="mb-1 mb-sm-2">{Utils.FormatCurrency(firestoreProduct.product.price, settings.currency)}</Card.Text>
                          )}

                          {/* Attribute Selection */}
                          {firestoreProduct.product.attributes && firestoreProduct.product.attributes.length > 0 && (
                            <Form.Group className="mb-1 mb-sm-2">
                              {Array.from(new Set(firestoreProduct.product.attributes.map(attr => attr.name))).sort().map((key) => (
                                <div key={key}>
                                  <Form.Select id={`attr-${firestoreProduct.id}-${key}`}
                                    required
                                    onChange={(e) => { e.target.classList.remove("is-invalid"); }}
                                  >
                                    <option value="" selected disabled>{key}</option>
                                    {firestoreProduct.product.attributes && firestoreProduct.product.attributes
                                      .filter(attr => attr.name === key)
                                      .sort((a, b) => a.value.localeCompare(b.value))
                                      .map((attr, index) => (
                                        <option key={index} value={attr.value}>{settings.hasPrice && attr.price && Number(attr.price) > 0 ? `${attr.value} (+${Utils.FormatCurrency(Number(attr.price), settings.currency)})` : attr.value}</option>
                                      ))}
                                  </Form.Select>
                                </div>
                              ))}
                            </Form.Group>
                          )}
                          {/* Hack to create hidden attributes to make product boxes same height */}
                          {maxAttributes > 0 && (!firestoreProduct.product.attributes || firestoreProduct.product.attributes.length < maxAttributes) && (
                            <Form.Group className="mb-1 mb-sm-3">
                              {Array(maxAttributes - (firestoreProduct.product.attributes && firestoreProduct.product.attributes.length > 0 ? firestoreProduct.product.attributes.length : 1)).fill(null).map((key, index) => (

                                <div key={`attr-${firestoreProduct.id}-${index}`}>
                                  <Form.Select style={{ "visibility": "hidden" }} id={`attr-${firestoreProduct.id}-${key}`}>
                                  </Form.Select>
                                </div>
                              ))}
                            </Form.Group>
                          )}

                          {/* Input Group for Quantity and Add to Cart Button */}
                            <InputGroup className="mt-auto w-100">
                              <Form.Control type="number" min="1" defaultValue="1" 
                              style={{ textAlign: "center" }} 
                              id={`quantity-${firestoreProduct.id}`} />
                              <Button
                                variant="primary"
                                style={{ whiteSpace: "nowrap" }}
                                className="d-none d-sm-flex rounded-end"
                                onClick={() => handleAddToCart(firestoreProduct)}
                                disabled={loadingState[firestoreProduct.id]}
                              >
                                {loadingState[firestoreProduct.id] ? <Spinner animation="border" size="sm" /> : "Add to Cart"}
                              </Button>
                              <Button
                                variant="primary"
                                style={{ whiteSpace: "nowrap" }}
                                className="d-sm-none"
                                onClick={() => handleAddToCart(firestoreProduct)}
                                disabled={loadingState[firestoreProduct.id]}
                              >
                                {loadingState[firestoreProduct.id] ? <Spinner animation="border" size="sm" /> : "+"}
                              </Button>
                            </InputGroup>                                                    
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            ) : null;
          })}

          {/* Pagination Controls */}
          <Pagination className="mt-4 justify-content-center">
            <Pagination.Prev
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            />
            {Array.from({ length: totalPages }, (_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === currentPage}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            />
          </Pagination>

        </Col>
        {/* <Col md={9}>
          <Row>
            {products
              .filter(product => !selectedCategory || product.category === selectedCategory)
              .map((product) => (
                <Col md={3} key={product.id}>
                  <Card
                    className="mb-4 p-3"
                    style={{ borderRadius: "15px", backgroundColor: "#f8f9fa", cursor: "pointer" }}
                    onClick={() => navigate(`/productdetails/${product.id}`)}
                  >
                    <Card.Img variant="top" src={product.imageUrl} />
                    <Card.Body>
                      <Card.Title>{product.name}</Card.Title>
                      <Card.Text>${product.price}</Card.Text>
                      <InputGroup>
                        <Form.Control type="number" min="1" defaultValue="1" style={{ maxWidth: "60px", textAlign: "center" }} />
                        <Button style={{ background: "transparent", border: "1px solid #BBB" }}>
                          <img src="/images/icons/cart.png" alt="Cart" style={{ width: "24px", height: "24px" }} />
                        </Button>
                      </InputGroup>


                    </Card.Body>
                  </Card>
                </Col>
              ))}
          </Row>
        </Col> */}
      </Row>
    </Container>
  );
};

export default ProductListing;